import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { RATE_UNITS, GENDERS, FOR_WHOM } from "@/lib/services";
import { UploadCloud, X, Loader2, Gift, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ScheduleEditor from "@/components/ScheduleEditor";

const inputCls = "w-full rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none focus:ring-1 focus:ring-rose-700";
const labelCls = "block text-sm font-medium text-zinc-300 mb-2";

function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" data-testid={`${testid}-${o}`} aria-pressed={value === o} onClick={() => onChange(o)}
          className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${value === o ? "border-rose-600 bg-rose-600 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

const EMPTY = {
  title: "", description: "", price: "", rate_unit: "per uur", category: "", location: "", images: [],
  availability: "", schedule: [], age: "", gender: "Vrouw", for_whom: "Iedereen", show_phone: false, contact_phone: "",
};

export default function CreateService() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upd = (k) => (e) => set(k, e.target.value);

  useEffect(() => {
    api.get("/service-categories").then(({ data }) => {
      setCategories(data);
      if (!editing) setForm((f) => ({ ...f, category: data[0] }));
    });
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    api.get(`/products/${id}/manage`).then(({ data }) => {
      const pick = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]]));
      setForm({ ...pick, price: String(data.price ?? ""), age: String(data.age ?? "") });
    }).catch(() => { toast.error("Kon dienst niet laden"); navigate("/dashboard"); });
  }, [editing, id, navigate]);

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || form.price === "" || form.images.length < 1) { toast.error("Vul titel en tarief in en voeg minimaal 1 foto toe"); return; }
    if (!form.age || Number(form.age) < 18) { toast.error("Je moet 18 jaar of ouder zijn"); return; }
    if (form.show_phone && !form.contact_phone.trim()) { toast.error("Vul je telefoonnummer in"); return; }
    setSaving(true);
    const payload = {
      ...form, listing_type: "service", condition: "", price: parseFloat(form.price), age: parseInt(form.age, 10),
      contact_method: form.show_phone ? "both" : "chat", contact_phone: form.show_phone ? form.contact_phone : "",
    };
    try {
      const { data } = editing ? await api.put(`/products/${id}`, payload) : await api.post("/products", payload);
      toast.success(editing ? "Wijzigingen opgeslagen" : "Je dienst staat direct online!");
      navigate(`/dienst/${data.id || id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon dienst niet opslaan");
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 mb-2">
        {editing ? <Pencil className="h-4 w-4 text-rose-400" /> : <Sparkles className="h-4 w-4 text-rose-400" />}
        <span className="font-mono text-xs uppercase tracking-widest text-rose-400">{editing ? "Dienst bewerken" : "Nieuwe dienst"}</span>
      </div>
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-8">{editing ? "Bewerk je dienst" : "Bied je dienst aan"}</h1>

      {!editing && (
        <div data-testid="free-service-banner" className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-800/50 bg-emerald-950/30 px-4 py-3">
          <Gift className="h-5 w-5 text-emerald-400 shrink-0" />
          <span className="text-sm text-emerald-200">Diensten plaatsen is nu <span className="font-semibold">gratis</span> — je advertentie staat direct online.</span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className={labelCls}>Foto's <span className="text-rose-400 font-normal">(minimaal 1)</span></label>
          <label data-testid="service-image-dropzone" className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-800 bg-zinc-900/40 py-10 cursor-pointer hover:border-rose-700/60 transition-colors">
            {uploading ? <Loader2 className="h-6 w-6 text-rose-400 animate-spin" /> : <UploadCloud className="h-6 w-6 text-zinc-500" />}
            <span className="text-sm text-zinc-400">{uploading ? "Uploaden..." : "Klik om foto's toe te voegen"}</span>
            <input data-testid="service-image-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          </label>
          {form.images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {form.images.map((p) => (
                <div key={p} className="relative h-24 w-20 overflow-hidden rounded-lg border border-zinc-800">
                  <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => set("images", form.images.filter((x) => x !== p))} className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Categorie</label>
          <Pills options={categories} value={form.category} onChange={(v) => set("category", v)} testid="service-category-option" />
        </div>

        <div>
          <label className={labelCls}>Titel / naam</label>
          <input data-testid="service-title-input" className={inputCls} value={form.title} onChange={upd("title")} placeholder="bijv. Sensuele bodymassage door Lisa" required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Tarief (€)</label>
            <input data-testid="service-price-input" type="number" step="0.01" min="0" className={inputCls} value={form.price} onChange={upd("price")} placeholder="bijv. 120" required />
          </div>
          <div>
            <label className={labelCls}>Per</label>
            <select data-testid="service-rate-unit-select" className={inputCls} value={form.rate_unit} onChange={upd("rate_unit")}>
              {RATE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Leeftijd</label>
            <input data-testid="service-age-input" type="number" min="18" max="99" className={inputCls} value={form.age} onChange={upd("age")} placeholder="18+" required />
          </div>
          <div>
            <label className={labelCls}>Locatie</label>
            <input data-testid="service-location-input" className={inputCls} value={form.location} onChange={upd("location")} placeholder="bijv. Rotterdam" />
          </div>
        </div>

        <div>
          <label className={labelCls}>Geslacht</label>
          <Pills options={GENDERS} value={form.gender} onChange={(v) => set("gender", v)} testid="service-gender-option" />
        </div>
        <div>
          <label className={labelCls}>Voor wie</label>
          <Pills options={FOR_WHOM} value={form.for_whom} onChange={(v) => set("for_whom", v)} testid="service-for-whom-option" />
        </div>

        <div>
          <label className={labelCls}>Weekrooster</label>
          <p className="mb-2 text-xs text-zinc-500">Vink de dagen aan waarop je werkt en kies je tijden.</p>
          <ScheduleEditor value={form.schedule} onChange={(v) => set("schedule", v)} />
        </div>

        <div>
          <label className={labelCls}>Opmerking over beschikbaarheid (optioneel)</label>
          <input data-testid="service-availability-input" className={inputCls} value={form.availability} onChange={upd("availability")} placeholder="bijv. ook op afspraak buiten deze tijden" />
        </div>

        <div>
          <label className={labelCls}>Omschrijving</label>
          <textarea data-testid="service-description-input" rows={5} className={inputCls} value={form.description} onChange={upd("description")} placeholder="Vertel iets over jezelf en wat je aanbiedt..." required />
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm text-zinc-200">Telefoonnummer tonen <span className="block text-xs text-zinc-500">Alleen zichtbaar voor ingelogde bezoekers. Chat is altijd mogelijk.</span></span>
            <input data-testid="service-show-phone-toggle" type="checkbox" checked={form.show_phone} onChange={(e) => set("show_phone", e.target.checked)} className="h-5 w-5 accent-rose-600" />
          </label>
          {form.show_phone && (
            <input data-testid="service-phone-input" className={inputCls} value={form.contact_phone} onChange={upd("contact_phone")} placeholder="06..." />
          )}
        </div>

        <button data-testid="submit-service-button" type="submit" disabled={saving}
          className="w-full rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3.5 shadow-lg shadow-rose-950/50 transition-transform hover:scale-[1.01] disabled:opacity-60">
          {saving ? "Bezig..." : editing ? "Wijzigingen opslaan" : "Plaats dienst gratis"}
        </button>
        <p className="text-center text-xs text-zinc-500">Door te plaatsen bevestig je dat je 18+ bent en je dienst legaal aanbiedt.</p>
      </form>
    </div>
  );
}
